/**
 * 301/302 Location 绝对化：强制 https + host（优先反代头）。
 * 忽略 x-forwarded-proto / 内网明文，避免 Location 落成 http://。
 */
import { getRequestHeader, type H3Event } from 'h3'
import { resolveTrustedRequestHost } from './trustedHost'

/** 301/302 Location 协议：强制 https */
export function resolveRequestProtocol(_event?: H3Event): string {
  return 'https'
}

/**
 * 当前请求 host：优先 x-forwarded-host，否则 host。
 * [防开放重定向] 经白名单校验，伪造的 x-forwarded-host 不会拼进 Location，
 * 校验失败静默回退可信域名（见 trustedHost.ts）。
 */
export function resolveRequestHostForRedirect(event: H3Event): string {
  const forwarded = getRequestHeader(event, 'x-forwarded-host')
  const hostHeader = getRequestHeader(event, 'host')
  return resolveTrustedRequestHost({
    'x-forwarded-host': forwarded,
    host: hostHeader
  })
}

/** 相对 Location → https + 当前 host 绝对 URL；已是绝对 URL 则原样返回 */
export function toAbsoluteRedirectLocation(event: H3Event, location: string): string {
  if (!location || /^https?:\/\//i.test(location)) return location
  const host = resolveRequestHostForRedirect(event)
  if (!host) return location
  const protocol = resolveRequestProtocol(event)
  const path = location.startsWith('/') ? location : `/${location}`
  return `${protocol}://${host}${path}`
}

/**
 * navigateTo / composable 用：SSR 读 request event；客户端强制 https + host。
 */
export function toAbsoluteRedirectLocationForNavigate(location: string): string {
  if (!location || /^https?:\/\//i.test(location)) return location
  if (import.meta.client && typeof window !== 'undefined') {
    const path = location.startsWith('/') ? location : `/${location}`
    return `https://${window.location.host}${path}`
  }
  if (import.meta.server) {
    try {
      const event = useRequestEvent()
      if (event) return toAbsoluteRedirectLocation(event, location)
    } catch {
      // no request event
    }
  }
  return location
}

/** path + query + hash → 绝对 301 Location */
export function buildAbsoluteRedirectHref(
  pathname: string,
  query?: Record<string, unknown>,
  hash?: string,
): string {
  const qs = new URLSearchParams()
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v == null || v === '') continue
      if (Array.isArray(v)) {
        v.forEach((item) => {
          if (item != null && item !== '') qs.append(k, String(item))
        })
      } else {
        qs.set(k, String(v))
      }
    }
  }
  const q = qs.toString()
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`
  return toAbsoluteRedirectLocationForNavigate(
    `${path}${q ? `?${q}` : ''}${hash || ''}`,
  )
}
